import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import Chart from "react-apexcharts";
import {
  MdBusiness,
  MdPeople,
  MdAttachMoney,
  MdSearch,
  MdTrendingUp,
  MdTrendingDown,
  MdCheckCircle,
  MdError,
  MdInfo,
  MdLocationOn,
  MdBlock,
} from "react-icons/md";
import {
  FaDollarSign,
  FaCoins,
  FaMoneyBillWave,
} from "react-icons/fa";
import { FaBuilding } from "react-icons/fa";
import { getOemNames } from "../redux/actions/oemActions";
import {
  getDashboardStatsRequest,
  getLicenseUsageRequest,
} from "../redux/actions/dashboardAction/dashboardAction";
import { getLicenses } from "../redux/actions/settingActions";
import {
  Container,
  Row,
  Col,
  Card,
  ProgressBar,
  Badge,
  Button,
} from "react-bootstrap";
import "../styles/Dashboard.css";
import "../styles/EnhancedDashboard.css";

const Dashboard = () => {
  const dispatch = useDispatch();
  const {
    stats: dashboardData,
    loading,
    error,
    licenseUsage,
    licenseUsageLoading,
    licenseUsageError,
  } = useSelector((state) => state.dashboard);
  const { licenses = [], loading: licensesLoading = false } = useSelector(
    (state) => state.setting || {}
  );
  const [oemFilter, setOemFilter] = useState(null);
  const [activeCard, setActiveCard] = useState(null);
  const [notifications, setNotifications] = useState(7);
  const [threatLevel, setThreatLevel] = useState("Medium");
  const [realTimeData, setRealTimeData] = useState([
    45, 67, 23, 89, 34, 78, 56,
  ]);
  const [isScanning, setIsScanning] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const rates = dashboardData?.conversionRates;
  const [lineChartData, setLineChartData] = useState(
    Array.from({ length: 24 }, (_, i) => Math.floor(Math.random() * 100))
  );
  const [areaChartData, setAreaChartData] = useState(
    Array.from({ length: 12 }, (_, i) => Math.floor(Math.random() * 80) + 20)
  );
  const [trafficData, setTrafficData] = useState({
    inbound: Array.from(
      { length: 10 },
      () => Math.floor(Math.random() * 50) + 10
    ),
    outbound: Array.from(
      { length: 10 },
      () => Math.floor(Math.random() * 40) + 5
    ),
  });
  // filter
  const { oemNames } = useSelector((state) => state.oem);
  useEffect(() => {
    dispatch(getOemNames());
  }, [dispatch]);
  // Check if filtered data has no results
  const hasNoData =
    oemFilter &&
    dashboardData?.response?.metrics &&
    dashboardData.response.metrics.oemPartnerCount === 0 &&
    dashboardData.response.metrics.oemRelatedUserCount === 0 &&
    dashboardData.response.metrics.scanCount === 0;

  // Use API data for security stats with fallback
  const securityStats = dashboardData?.metrics
    ? [
        {
          title: "OEM Partners",
          value: (dashboardData?.metrics?.oemPartnerCount || 0).toString(),
          trend: "up",
          icon: MdBusiness,
          color: "#10b981",
          status: "success",
        },
        {
          title: "OEM Users",
          value: (dashboardData?.metrics?.oemRelatedUserCount || 0).toString(),
          trend: "up",
          icon: MdPeople,
          color: "#3b82f6",
          status: "info",
        },
        {
          title: "Total Scans",
          value: (dashboardData?.metrics?.scanCount || 0).toString(),
          trend: "up",
          icon: MdSearch,
          color: "#f59e0b",
          status: "warning",
        },
        {
          title: "Total Licenses",
          value: (dashboardData?.metrics?.licenseCount || 0).toString(),
          trend: "up",
          icon: FaCoins,
          color: "#28a745",
          status: "critical",
        },
        {
          title: "Assigned Licenses",
          value: (dashboardData?.metrics?.assignedLicenseCount || 0).toString(),
          trend: "up",
          icon: FaCoins,
          color: "#6f42c1",
          status: "critical",
        },
        {
          title: "Available Licenses",
          value: (
            dashboardData?.metrics?.availableLicenseCount || 0
          ).toString(),
          trend: "up",
          icon: FaMoneyBillWave,
          color: "#fd7e14",
          status: "critical",
        },
      ]
    : [
        {
          title: "OEM Partners",
          value: "0",
          change: "+0%",
          trend: "up",
          icon: MdBusiness,
          color: "#10b981",
          status: "success",
        },
        {
          title: "OEM Users",
          value: "0",
          change: "+0%",
          trend: "up",
          icon: MdPeople,
          color: "#3b82f6",
          status: "info",
        },
        {
          title: "Total Scans",
          value: "0",
          change: "+0%",
          trend: "up",
          icon: MdSearch,
          color: "#f59e0b",
          status: "warning",
        },
        {
          title: "Total Licenses",
          value: "0",
          change: "+0%",
          trend: "up",
          icon: FaCoins,
          color: "#ef4444",
          status: "critical",
        },
      ];

  // Use API data for plan distribution
  const pieChartData = dashboardData?.metrics?.planDistribution
    ? [
        {
          label: "Per Report",
          value:
            dashboardData?.metrics?.planDistribution?.monthly?.percentage || 0,
          count: dashboardData?.metrics?.planDistribution?.monthly?.count || 0,
          color: "#3b82f6",
        },
        {
          label: "Annual Plans",
          value:
            dashboardData?.metrics?.planDistribution?.annual?.percentage || 0,
          count: dashboardData?.metrics?.planDistribution?.annual?.count || 0,
          color: "#10b981",
        },
      ].filter((item) => item.value > 0)
    : [];

  // Use API data for language distribution
  const languageData = dashboardData?.languageDistribution
    ? [
        {
          label: "English",
          value: dashboardData?.languageDistribution?.eng || 0,
          color: "#3b82f6",
          flag: "🇺🇸",
          code: "EN",
        },
      ].filter((item) => item.value > 0)
    : [];

  const totalLanguageUsers = languageData.reduce(
    (sum, lang) => sum + lang.value,
    0
  );

  const hasValidPieData =
    pieChartData.length > 0 && pieChartData.some((item) => item.value > 0);

  // Use API data for country locations
  const countryCoordinates = {
    India: { lat: 20.5937, lng: 78.9629 },
    "United States": { lat: 39.8283, lng: -98.5795 },
    "United Kingdom": { lat: 55.3781, lng: -3.436 },
    Japan: { lat: 36.2048, lng: 138.2529 },
    Australia: { lat: -25.2744, lng: 133.7751 },
    Comoros: { lat: -11.6455, lng: 43.3333 },
    "Falkland Islands": { lat: -51.7963, lng: -59.5236 },
    Sudan: { lat: 12.8628, lng: 30.2176 },
    Zimbabwe: { lat: -19.0154, lng: 29.1549 },
    "Trinidad and Tobago": { lat: 10.6918, lng: -61.2225 },
  };

  const trafficLocations =
    dashboardData?.usersByOemPartnerCountry?.map((location) => ({
      country: location.country,
      users: location.users,
      oems: location.oems,
      total: location.total,
      lat: countryCoordinates[location.country]?.lat || 0,
      lng: countryCoordinates[location.country]?.lng || 0,
    })) || [];
  useEffect(() => {
    // Get itSalesId from sessionStorage
    const userData = JSON.parse(sessionStorage.getItem("user") || "{}");
    const itSalesId = userData?.itSalesPartner?.id;

    if (itSalesId) {
      const oemId = oemFilter?.value || null;
      dispatch(getDashboardStatsRequest(itSalesId, oemId));

      // Fetch licenses for the dashboard
      dispatch(
        getLicenses({
          page: 1,
          limit: 10,
          search: "",
          status: "active",
        })
      );
    }
  }, [dispatch, oemFilter]);

  useEffect(() => {
    const interval = setInterval(() => {
      setRealTimeData((prev) =>
        prev.map(() => Math.floor(Math.random() * 100))
      );
      setNotifications((prev) =>
        Math.max(0, prev + Math.floor(Math.random() * 3) - 1)
      );
      setLineChartData((prev) => [
        ...prev.slice(1),
        Math.floor(Math.random() * 100),
      ]);
      setAreaChartData((prev) => [
        ...prev.slice(1),
        Math.floor(Math.random() * 80) + 20,
      ]);
      setTrafficData((prev) => ({
        inbound: [
          ...prev.inbound.slice(1),
          Math.floor(Math.random() * 50) + 10,
        ],
        outbound: [
          ...prev.outbound.slice(1),
          Math.floor(Math.random() * 40) + 5,
        ],
      }));
      // Update threat levels
      trafficLocations.forEach((location) => {
        const change = Math.floor(Math.random() * 6) - 3;
        location.threats = Math.max(5, Math.min(70, location.threats + change));
      });
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    let scanInterval;
    if (isScanning) {
      scanInterval = setInterval(() => {
        console.log("Scanning for threats...");
      }, 2000);
    }
    return () => clearInterval(scanInterval);
  }, [isScanning]);

  const updateThreatData = () => {
    const levels = ["Low", "Medium", "High", "Critical"];
    setThreatLevel(levels[Math.floor(Math.random() * levels.length)]);
  };

  const getSeverityColor = (severity) => {
    switch (severity.toLowerCase()) {
      case "critical":
        return "#ef4444";
      case "high":
        return "#f59e0b";
      case "medium":
        return "#3b82f6";
      case "low":
        return "#10b981";
      default:
        return "#6b7280";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "active":
        return <MdError style={{ color: "#ef4444" }} />;
      case "blocked":
        return <MdBlock style={{ color: "#6b7280" }} />;
      case "mitigated":
        return <MdCheckCircle style={{ color: "#10b981" }} />;
      case "investigating":
        return <MdInfo style={{ color: "#3b82f6" }} />;
      default:
        return <MdInfo />;
    }
  };

  // Use API data for recent scans
  const recentScans = dashboardData?.recentScans || [];
  const filteredScans = recentScans.filter((scan) => {
    const matchesSearch =
      scan?.domain?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      scan?.user?.email?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  // Use API data for recent transactions
  const recentTransactions = dashboardData?.recentTransactions || [];

  // Use API data for top OEM partners
  const topRevenuePartners = dashboardData?.topOemPartners || [];

  // Use API data for top domains
  const topDomains = dashboardData?.topDomains || [];

  // Use API data for scan analytics
  const scanAnalytics = dashboardData?.scanAnalytics || {
    completed: 0,
    pending: 0,
    failed: 0,
    total: 0,
    completionRate: 0,
  };

  // Use API data for monthly growth trends
  const monthlyGrowthTrends = dashboardData?.monthlyGrowthTrends || {
    users: [],
    scans: [],
  };

  // Use API data for conversion rates
  const conversionRates = dashboardData?.conversionRates || {
    verificationRate: 0,
  };

  // Show loading state
  if (loading) {
    return (
      <div className="page-container">
        <div className="page-card scroll-container">
          <Container
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "100vh",
              flexDirection: "column",
            }}
          >
            <div className="spinner-enhanced mb-3"></div>
            <p style={{ color: "#7f8c8d", fontSize: "14px" }}>
              Loading Dashboard...
            </p>
          </Container>
        </div>
      </div>
    );
  }
  // Show error state
  if (error) {
    return (
      <div className="page-container">
        <div className="page-card scroll-container">
          <Container>
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ minHeight: "400px" }}
            >
              <div className="text-center loading-enhanced">
                <MdError size={48} className="text-danger mb-3" />
                <p className="text-muted mb-3 fw-medium">
                  Error loading dashboard: {error}
                </p>
                <Button
                  variant="primary"
                  className="badge-enhanced"
                  onClick={() => {
                    const userData = JSON.parse(
                      sessionStorage.getItem("user") || "{}"
                    );
                    const itSalesId = userData?.itSalesPartner?.id;
                    if (itSalesId) {
                      dispatch(
                        getDashboardStatsRequest(
                          itSalesId,
                          new Date().getMonth() + 1
                        )
                      );
                    }
                  }}
                >
                  Retry
                </Button>
              </div>
            </div>
          </Container>
        </div>
      </div>
    );
  }
  return (
    <div className="page-container">
      <div className="page-card scroll-container">
        <div className="dashboard-enhanced">
          <Container fluid className="py-2 px-3">
            {/* Header with Filter */}
            <Row className="mb-3 align-items-center">
              <Col md={7}>
                <h4
                  className="fw-bold text-dark mb-0"
                  style={{ fontSize: "16px" }}
                >
                  Dashboard Overview
                </h4>
                <p className="text-muted mb-0 small">
                  Monitor your OEM partners
                </p>
              </Col>
              <Col md={5}>
                <div className="d-flex align-items-center gap-3 justify-content-end">
                  <div
                    style={{
                      minWidth: "200px",
                      position: "relative",
                    }}
                  >
                    <label
                      className="form-label fw-bold small mb-0"
                      style={{ fontSize: "12px" }}
                    >
                      Filters by OEM
                    </label>
                    <Select
                      value={oemFilter}
                      onChange={setOemFilter}
                      options={oemNames?.map((oem) => ({
                        value: oem._id,
                        label: oem.companyName,
                      }))}
                      placeholder="All OEM Partners"
                      isClearable
                      isSearchable
                      classNamePrefix="react-select"
                      menuPortalTarget={document.body}
                      styles={{
                        control: (base, state) => ({
                          ...base,
                          minHeight: "42px",
                          border: "2px solid rgba(255, 255, 255, 0.3)",
                          borderRadius: "8px",
                          backgroundColor: "white",
                          boxShadow: state.isFocused
                            ? "0 0 0 3px rgba(255, 255, 255, 0.4)"
                            : "0 2px 8px rgba(0,0,0,0.1)",
                          "&:hover": {
                            border: "2px solid rgba(255, 255, 255, 0.5)",
                          },
                          transition: "all 0.2s ease",
                        }),
                        menuPortal: (base) => ({
                          ...base,
                          zIndex: 9999,
                        }),
                        menu: (base) => ({
                          ...base,
                          zIndex: 9999,
                          backgroundColor: "white",
                          boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
                          border: "1px solid #e5e7eb",
                        }),
                        placeholder: (base) => ({
                          ...base,
                          color: "#6b7280",
                          fontWeight: "500",
                        }),
                      }}
                    />
                  </div>
                </div>
              </Col>
            </Row>
            {/* Stats Cards */}
            <Row className="mb-2">
              {securityStats.map((stat, index) => (
                <Col key={index} md={2} className="mb-3">
                  <Card
                    className="h-100 stats-card-enhanced card-enhanced fade-in"
                    // style={{
                    //   "--card-accent-color":
                    //     "linear-gradient(135deg, #1e40af, #3b82f6)",
                    //   animationDelay: `${index * 0.1}s`,
                    // }}
                  >
                    <Card.Body className="d-flex align-items-center">
                      <div
                        className="rounded-circle d-flex align-items-center justify-content-center me-3 stats-icon-enhanced"
                        style={{
                          width: "35px",
                          height: "35px",
                          backgroundColor: `${stat.color}15`,
                          color: stat.color,
                          background: `linear-gradient(135deg, ${stat.color}15 0%, ${stat.color}25 100%)`,
                        }}
                      >
                        <stat.icon size={26} />
                      </div>
                      <div className="d-flex justify-content-start align-items-center mb-1">
                        <h4 className="mb-0 fw-bold text">{stat.value}</h4>
                        <p
                          className="text-muted mb-0 small fw-medium ms-2"
                          style={{ fontSize: "14px" }}
                        >
                          {stat.title}
                        </p>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
            {/* Charts Row */}
            <Row className="mb-2">
              <Col md={6} className="mb-3">
                <Card className="h-100 chart-card-enhanced card-enhanced fade-in">
                  <Card.Header className="chart-header-enhanced">
                    <div className="d-flex justify-content-between align-items-center">
                      <h6 className="mb-0 fw-bold">🌐 Language Distribution</h6>
                    </div>
                  </Card.Header>
                  <Card.Body className="py-4">
                    {languageData.length > 0 ? (
                      <div className="enhanced-bar-container">
                        <Chart
                          options={{
                            chart: {
                              type: "bar",
                              toolbar: { show: false },
                              animations: {
                                enabled: true,
                                easing: "easeinout",
                                speed: 1000,
                                animateGradually: {
                                  enabled: true,
                                  delay: 150,
                                },
                              },
                            },
                            plotOptions: {
                              bar: {
                                horizontal: true,
                                borderRadius: 8,
                                borderRadiusApplication: "end",
                                barHeight: "60%",
                                distributed: true,
                              },
                            },
                            colors: [
                              "#3b82f6",
                              "#10b981",
                              "#f59e0b",
                              "#ef4444",
                            ],
                            xaxis: {
                              categories: languageData.map(
                                (item) => `${item.flag} ${item.label}`
                              ),
                              labels: {
                                style: {
                                  fontSize: "13px",
                                  fontWeight: 500,
                                  colors: "#374151",
                                },
                              },
                              axisBorder: {
                                show: false,
                              },
                              axisTicks: {
                                show: false,
                              },
                            },
                            yaxis: {
                              labels: {
                                style: {
                                  fontSize: "13px",
                                  fontWeight: 600,
                                  colors: "#374151",
                                },
                              },
                            },
                            dataLabels: {
                              enabled: true,
                              formatter: function (val, opts) {
                                const item = languageData[opts.dataPointIndex];
                                return item.value + " users";
                              },
                              style: {
                                fontSize: "12px",
                                fontWeight: 600,
                                colors: ["#fff"],
                              },
                              dropShadow: {
                                enabled: true,
                                top: 1,
                                left: 1,
                                blur: 1,
                                color: "#000",
                                opacity: 0.3,
                              },
                            },
                            tooltip: {
                              enabled: true,
                              y: {
                                formatter: function (val, opts) {
                                  const percentage = (
                                    (val / totalLanguageUsers) *
                                    100
                                  ).toFixed(1);
                                  return val + " users (" + percentage + "%)";
                                },
                              },
                              style: {
                                fontSize: "13px",
                              },
                            },
                            grid: {
                              show: true,
                              borderColor: "#f1f5f9",
                              strokeDashArray: 3,
                              xaxis: {
                                lines: {
                                  show: true,
                                },
                              },
                              yaxis: {
                                lines: {
                                  show: false,
                                },
                              },
                            },
                            legend: {
                              show: false,
                            },
                            states: {
                              hover: {
                                filter: {
                                  type: "lighten",
                                  value: 0.1,
                                },
                              },
                            },
                          }}
                          series={[
                            {
                              name: "Users",
                              data: languageData.map((item) => item.value),
                            },
                          ]}
                          type="bar"
                          height={280}
                        />
                      </div>
                    ) : (
                      <div className="d-flex align-items-center justify-content-center h-100">
                        <div className="text-center">
                          <div
                            className="mb-3"
                            style={{ fontSize: "48px", opacity: 0.3 }}
                          >
                            🌐
                          </div>
                          <p className="text-muted mb-0 fw-medium">
                            No language data available
                          </p>
                        </div>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6} className="mb-3">
                <Card className="h-100 chart-card-enhanced card-enhanced fade-in">
                  <Card.Header className="chart-header-enhanced">
                    <h6 className="mb-0 fw-bold">📊 Scan Analytics</h6>
                  </Card.Header>
                  <Card.Body>
                    <Row className="g-2">
                      <Col xs={6}>
                        <div
                          className="text-center p-3"
                          style={{
                            backgroundColor: "#f8f9fa",
                            borderRadius: "8px",
                            border: "1px solid #e9ecef",
                          }}
                        >
                          <div className="h4 text-success mb-1 fw-bold">
                            {scanAnalytics.completed}
                          </div>
                          <small className="text-muted fw-medium">
                            Completed
                          </small>
                        </div>
                      </Col>
                      <Col xs={6}>
                        <div
                          className="text-center p-3"
                          style={{
                            backgroundColor: "#fff3cd",
                            borderRadius: "8px",
                            border: "1px solid #ffeaa7",
                          }}
                        >
                          <div
                            className="h4 mb-1 fw-bold"
                            style={{ color: "#856404" }}
                          >
                            {scanAnalytics.pending}
                          </div>
                          <small className="text-muted fw-medium">
                            Pending
                          </small>
                        </div>
                      </Col>
                      <Col xs={6}>
                        <div
                          className="text-center p-3"
                          style={{
                            backgroundColor: "#f8d7da",
                            borderRadius: "8px",
                            border: "1px solid #f5c6cb",
                          }}
                        >
                          <div className="h4 text-danger mb-1 fw-bold">
                            {scanAnalytics.failed}
                          </div>
                          <small className="text-muted fw-medium">Failed</small>
                        </div>
                      </Col>
                      <Col xs={6}>
                        <div
                          className="text-center p-3"
                          style={{
                            backgroundColor: "#d1ecf1",
                            borderRadius: "8px",
                            border: "1px solid #bee5eb",
                          }}
                        >
                          <div className="h4 text-info mb-1 fw-bold">
                            {scanAnalytics.completionRate}%
                          </div>
                          <small className="text-muted fw-medium">
                            Success Rate
                          </small>
                        </div>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
            {/* Main Content Row */}
            <Row className="mb-2">
              <Col lg={8}>
                <Row>
                  <Col lg={12}>
                    {/* Global User Distribution */}
                    <Card className="mb-4 chart-card-enhanced card-enhanced fade-in">
                      <Card.Header className="chart-header-enhanced">
                        <h6 className="mb-0 fw-semibold text-gradient">
                          🗺️ Global User Distribution
                        </h6>
                      </Card.Header>
                      <Card.Body style={{ overflowY: "auto", height: "260px" }}>
                        {trafficLocations.length > 0 ? (
                          <Row>
                            {trafficLocations.map((location, index) => (
                              <Col key={index} md={6} lg={4} className="mb-3">
                                <div
                                  className="location-card-enhanced p-3 h-100"
                                  style={{
                                    backgroundColor: "#ffffff",
                                    borderRadius: "8px",
                                    transition: "all 0.3s ease",
                                    cursor: "pointer",
                                  }}
                                  onMouseEnter={(e) => {
                                    const card = e.currentTarget;
                                    card.style.backgroundColor = "#ffffff";
                                    card.style.transform = "translateY(-2px)";
                                    card.style.boxShadow =
                                      "0 8px 25px rgba(0,0,0,0.15)";
                                  }}
                                  onMouseLeave={(e) => {
                                    const card = e.currentTarget;
                                    card.style.backgroundColor = "#ffffff";
                                    card.style.transform = "translateY(0)";
                                    card.style.boxShadow = "none";
                                  }}
                                >
                                  <div className="d-flex justify-content-between align-items-center mb-2">
                                    <h6 className="mb-0 fw-semibold">
                                      {location.country}
                                    </h6>
                                    <MdLocationOn
                                      className="text-primary"
                                      size={20}
                                    />
                                  </div>
                                  <div className="small text-muted">
                                    <div>
                                      Users:{" "}
                                      <span className="fw-bold text-primary">
                                        {location.users}
                                      </span>
                                    </div>
                                    <div>
                                      Total:{" "}
                                      <span className="fw-bold text-success">
                                        {location.total}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </Col>
                            ))}
                          </Row>
                        ) : (
                          <div className="text-center text-muted py-4">
                            <p className="mb-0">
                              No global distribution data available
                            </p>
                          </div>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </Col>
              <Col lg={4}>
                {/* Recent Scans */}
                <Card className="mb-4 chart-card-enhanced card-enhanced fade-in">
                  <Card.Header className="chart-header-enhanced">
                    <h6 className="mb-0 fw-semibold text-gradient">
                      🔍 Recent Scans
                    </h6>
                  </Card.Header>
                  <Card.Body style={{ height: "260px", overflowY: "auto" }}>
                    {recentScans.length > 0 ? (
                      recentScans.slice(0, 5).map((scan, index) => (
                        <div
                          key={scan._id || index}
                          className="scan-item-enhanced mb-3"
                          style={{
                            "--scan-status-color":
                              scan.status === "completed"
                                ? "#10b981"
                                : "#6b7280",
                            animationDelay: `${index * 0.05}s`,
                          }}
                        >
                          <div className="d-flex align-items-start gap-3">
                            <div className="mt-1">
                              {scan.status === "completed" ? (
                                <MdCheckCircle
                                  className="text-success"
                                  size={20}
                                />
                              ) : (
                                <MdInfo className="text-muted" size={20} />
                              )}
                            </div>
                            <div className="flex-grow-1">
                              <div className="d-flex justify-content-between align-items-start mb-2">
                                <div>
                                  <div className="fw-semibold">
                                    {scan.domain}
                                  </div>
                                  <div className="small text-muted">
                                    ID:{" "}
                                    {(scan._id || "N/A").toString().slice(-8)}
                                  </div>
                                </div>
                                <Badge
                                  style={{
                                    backgroundColor: scan.overAllRiskScore
                                      ? scan.overAllRiskScore > 30
                                        ? "#ef4444"
                                        : scan.overAllRiskScore > 15
                                        ? "#f59e0b"
                                        : "#10b981"
                                      : "#ffc107",
                                    color: "white !important",
                                    border: "none !important",
                                    fontSize: "0.75rem",
                                    padding: "0.25rem 0.5rem",
                                    borderRadius: "0.375rem",
                                  }}
                                  className="small"
                                >
                                  {scan.overAllRiskScore
                                    ? `Risk: ${scan.overAllRiskScore.toFixed(
                                        1
                                      )}`
                                    : scan.status === "completed"
                                    ? "Completed"
                                    : "Pending"}
                                </Badge>
                              </div>
                              <div className="small text-muted d-flex align-items-start justify-content-between">
                                <div className="fw-medium">
                                  <p className="mb-0">
                                    {scan.user?.firstName || "N/A"}{" "}
                                    {scan.user?.lastName || ""}
                                  </p>
                                  <div className="d-flex align-items-center gap-1">
                                    <FaBuilding size={12} />{" "}
                                    {scan.oemPartner?.companyName ||
                                      scan.user?.email ||
                                      "N/A"}
                                  </div>
                                </div>
                                <div>
                                  {scan.createdAt
                                    ? new Date(
                                        scan.createdAt
                                      ).toLocaleDateString()
                                    : "N/A"}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center text-muted py-4">
                        <p className="mb-0">No recent scans available</p>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
            <Row className="mb-2">
              <Col lg={8}>
                {/* Top Revenue OEM Partners */}
                <Card className="mb-4 chart-card-enhanced card-enhanced fade-in">
                  <Card.Header className="chart-header-enhanced">
                    <h6 className="mb-0 fw-semibold text-gradient">
                      🏆 Top Revenue OEM Partners
                    </h6>
                  </Card.Header>
                  <Card.Body style={{ height: "350px", overflowY: "auto" }}>
                    {topRevenuePartners && topRevenuePartners.length > 0 ? (
                      <Row>
                        {topRevenuePartners.map((partner, index) => (
                          <Col key={partner._id} md={6} lg={4} className="mb-3">
                            <Card
                              className="h-100 partner-card-enhanced"
                              style={{ animationDelay: `${index * 0.1}s` }}
                            >
                              <Card.Body className="text-center">
                                <Row className="align-items-center">
                                  <Col
                                    md={6}
                                    className="d-flex flex-column align-items-center"
                                  >
                                    <h6 className="fw-bold mb-2">
                                      {partner.oemPartner?.companyName}
                                    </h6>
                                    <div className="small text-muted">
                                      Users:{" "}
                                      <span className="fw-bold text-primary">
                                        {partner.userCount}
                                      </span>
                                    </div>
                                  </Col>
                                  <Col md={6} className="text-center">
                                    <div className="fw-bold text-success fs-5">
                                      {partner.scanCount}
                                    </div>
                                    <div className="text-muted small">
                                      Scans
                                    </div>
                                  </Col>
                                </Row>
                              </Card.Body>
                            </Card>
                          </Col>
                        ))}
                      </Row>
                    ) : (
                      <div className="text-center text-muted py-4">
                        <p className="mb-0">No revenue data available</p>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
              <Col lg={4}>
                <Row>
                  <Col lg={12}>
                    {/* Conversion Metrics */}
                    <Card className="mb-4 chart-card-enhanced card-enhanced fade-in">
                      <Card.Header className="chart-header-enhanced">
                        <h6 className="mb-0 fw-semibold text-gradient">
                          📊 Conversion Metrics
                        </h6>
                      </Card.Header>
                      <Card.Body>
                        <div className="mb-3">
                          <div className="d-flex justify-content-between mb-2">
                            <span className="text-muted small">
                              Verification Rate
                            </span>
                            <span className="fw-medium small">
                              {conversionRates.verificationRate || 0}%
                            </span>
                          </div>
                          <div className="progress-enhanced">
                            <div
                              className="progress-bar progress-bar-enhanced"
                              style={{
                                width: `${Math.min(
                                  conversionRates.verificationRate || 0,
                                  100
                                )}%`,
                                "--progress-color": "#198754",
                                "--progress-color-light": "#75b798",
                                backgroundColor: "#198754",
                                height: "8px",
                                borderRadius: "4px",
                                transition: "width 0.6s ease",
                              }}
                            ></div>
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col lg={12}>
                    {/* Top Domains */}
                    <Card className="mb-4 chart-card-enhanced card-enhanced fade-in">
                      <Card.Header className="chart-header-enhanced">
                        <h6 className="mb-0 fw-semibold text-gradient">
                          🌐 Top Domains
                        </h6>
                      </Card.Header>
                      <Card.Body style={{ height: "180px", overflowY: "auto" }}>
                        {topDomains && topDomains.length > 0 ? (
                          topDomains.map((domain, index) => {
                            const maxCount = Math.max(
                              ...topDomains.map(
                                (d) => d.count || d.scanCount || 0
                              )
                            );
                            const scanCount =
                              domain.count || domain.scanCount || 0;
                            const percentage =
                              maxCount > 0 ? (scanCount / maxCount) * 100 : 0;
                            const colors = [
                              "#0d6efd",
                              "#198754",
                              "#ffc107",
                              "#17a2b8",
                              "#6c757d",
                            ];
                            const lightColors = [
                              "#6ea8fe",
                              "#75b798",
                              "#ffda6a",
                              "#6edff6",
                              "#adb5bd",
                            ];

                            return (
                              <div key={index} className="mb-3">
                                <div className="d-flex justify-content-between mb-1">
                                  <span className="fw-medium">
                                    {domain.name || domain.domain}
                                  </span>
                                  <span className="text-muted small">
                                    {scanCount}
                                  </span>
                                </div>
                                <div className="domain-progress-enhanced">
                                  <div
                                    className="progress-bar"
                                    style={{
                                      width: `${percentage}%`,
                                      "--domain-color": colors[index % 5],
                                      "--domain-color-light":
                                        lightColors[index % 5],
                                      backgroundColor: colors[index % 5],
                                      height: "8px",
                                      borderRadius: "4px",
                                      transition: "width 0.6s ease",
                                    }}
                                  ></div>
                                </div>
                              </div>
                            );
                          })
                        ) : (
                          <div className="text-center text-muted py-4">
                            <p className="mb-0">No domain data available</p>
                          </div>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </Col>
            </Row>
            {/* License Usage & Monthly Growth Trends */}
            <Row className="mb-2">
              <Col lg={6}>
                {/* License Usage */}
                <Card className="chart-card-enhanced card-enhanced fade-in">
                  <Card.Header className="chart-header-enhanced">
                    <h6 className="mb-0 fw-semibold text-gradient">
                      📋 License Management
                    </h6>
                  </Card.Header>
                  <Card.Body
                    style={{
                      height: "395px",
                      overflowY: "scroll",
                      scrollbarWidth: "none",
                      msOverflowStyle: "none",
                    }}
                    className="hide-scrollbar"
                  >
                    {licensesLoading ? (
                      <div className="text-center text-muted py-4">
                        <div className="spinner-border spinner-border-sm me-2"></div>
                        Loading licenses...
                      </div>
                    ) : licenses &&
                      Array.isArray(licenses) &&
                      licenses.length > 0 ? (
                      <div>
                        {licenses.map((license, index) => (
                          <div
                            key={license._id}
                            className="mb-3 p-3 rounded"
                            style={{
                              backgroundColor: "#f8f9fa",
                              border: "1px solid #e9ecef",
                              transition: "all 0.2s ease",
                            }}
                          >
                            <div className="d-flex justify-content-between align-items-start mb-2">
                              <div className="flex-grow-1">
                                <h6 className="fw-bold mb-1 text-primary">
                                  {license.name}
                                </h6>
                                <small className="text-muted font-monospace">
                                  {license.licenseKey}
                                </small>
                              </div>
                              <Badge
                                bg={
                                  license.status === "active"
                                    ? "success"
                                    : "danger"
                                }
                              >
                                {license.status || "N/A"}
                              </Badge>
                            </div>

                            {license.assigned && license.partnerAssigned && (
                              <div
                                className="mb-2 p-2 rounded"
                                style={{ backgroundColor: "#e3f2fd" }}
                              >
                                <div className="d-flex align-items-center mb-1">
                                  <MdBusiness
                                    className="me-2 text-primary"
                                    size={16}
                                  />
                                  <strong className="text-primary">
                                    Assigned to Partner
                                  </strong>
                                </div>
                                <small className="text-muted">
                                  Partner assigned
                                </small>
                              </div>
                            )}

                            <div className="row text-center mt-3">
                              <div className="col-3">
                                <Badge
                                  bg={
                                    license.assigned ? "success" : "secondary"
                                  }
                                  className="mb-1"
                                >
                                  {license.assigned ? "Assigned" : "Free"}
                                </Badge>
                                <div className="text-muted small">Status</div>
                              </div>
                              <div className="col-3">
                                <Badge
                                  bg={
                                    license.partnerAssigned
                                      ? "info"
                                      : "secondary"
                                  }
                                  className="mb-1"
                                >
                                  {license.partnerAssigned
                                    ? "Partner"
                                    : "No Partner"}
                                </Badge>
                                <div className="text-muted small">Partner</div>
                              </div>
                              <div className="col-3">
                                <div className="fw-medium small">
                                  {license.startDate
                                    ? new Date(
                                        license.startDate
                                      ).toLocaleDateString()
                                    : "N/A"}
                                </div>
                                <div className="text-muted small">
                                  Start Date
                                </div>
                              </div>
                              <div className="col-3">
                                <div className="fw-medium small">
                                  {license.endDate
                                    ? new Date(
                                        license.endDate
                                      ).toLocaleDateString()
                                    : "N/A"}
                                </div>
                                <div className="text-muted small">End Date</div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center text-muted py-4">
                        <div
                          style={{
                            fontSize: "48px",
                            opacity: 0.3,
                            marginBottom: "16px",
                          }}
                        >
                          📋
                        </div>
                        <p className="mb-0">No license data available</p>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
              <Col lg={6}>
                {/* Monthly Growth Trends */}
                <Card className="chart-card-enhanced card-enhanced fade-in">
                  <Card.Header className="chart-header-enhanced">
                    <h6 className="mb-0 fw-semibold text-gradient">
                      📊 Monthly Growth Trends
                    </h6>
                  </Card.Header>
                  <Card.Body
                    style={{
                      height: "395px",
                      overflowY: "scroll",
                      scrollbarWidth: "none",
                      msOverflowStyle: "none",
                    }}
                  >
                    <MonthlyGrowthChart data={monthlyGrowthTrends} />
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Container>
        </div>
      </div>
    </div>
  );
};

// Monthly Growth Chart Component
const MonthlyGrowthChart = ({ data }) => {
  if (
    !data ||
    !data.users ||
    !Array.isArray(data.users) ||
    data.users.length === 0
  ) {
    return (
      <div className="text-center text-muted py-4">
        <p className="mb-0">No monthly growth data available</p>
      </div>
    );
  }

  const getMonthName = (monthId) => {
    if (!monthId) return "";
    const [year, month] = monthId.split("-");
    const date = new Date(year, month - 1);
    return date.toLocaleDateString("en-US", { month: "short" });
  };

  const chartData = {
    users: Array.isArray(data.users) ? data.users : [],
    scans: Array.isArray(data.scans) ? data.scans : [],
    revenue: Array.isArray(data.revenue) ? data.revenue : [],
  };

  const categories = chartData.users.map((item) => getMonthName(item?._id));

  return (
    <Chart
      options={{
        chart: {
          type: "line",
          height: 350,
          toolbar: { show: false },
        },
        colors: [
          "#0d6efd",
          "#198754",
          "#ffc107",
          "#dc3545",
          "#6f42c1",
          "#fd7e14",
          "#20c997",
        ],
        stroke: {
          curve: "smooth",
          width: 3,
        },
        xaxis: {
          categories: categories,
        },
        yaxis: [
          {
            title: { text: "Users & Scans" },
            labels: { style: { colors: "#0d6efd" } },
          },
          {
            opposite: true,
            title: { text: "Revenue (Multi-Currency)" },
            labels: { style: { colors: "#ffc107" } },
          },
        ],
        legend: {
          position: "top",
          horizontalAlign: "center",
          offsetY: -5,
          itemMargin: {
            horizontal: 15,
            vertical: 5,
          },
        },
        grid: {
          borderColor: "#f1f1f1",
        },
      }}
      series={[
        {
          name: "Users",
          data: chartData.users.map((item) => item?.count || 0),
          yAxisIndex: 0,
        },
        {
          name: "Scans",
          data: chartData.scans.map((item) => item?.count || 0),
          yAxisIndex: 0,
        },
        {
          name: "Revenue (USD)",
          data: chartData.revenue.map((item) => item?.usd || 0),
          yAxisIndex: 1,
        },
        {
          name: "Revenue (SGD)",
          data: chartData.revenue.map((item) => item?.sgd || 0),
          yAxisIndex: 1,
        },
        {
          name: "Revenue (HKD)",
          data: chartData.revenue.map((item) => item?.hkd || 0),
          yAxisIndex: 1,
        },
        {
          name: "Revenue (AUD)",
          data: chartData.revenue.map((item) => item?.aud || 0),
          yAxisIndex: 1,
        },
      ]}
      type="line"
      height={350}
    />
  );
};

export default Dashboard;
